'use client';

import * as React from 'react';
import { Check, X, Search } from 'lucide-react';

import { cn } from '@/lib/utils';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';

export type AutocompleteOption = {
  value: string;
  label: string;
  description?: string;
  group?: string;
};

export type SelectedItem = {
  value: string;
  label: string;
  description?: string;
};

interface AutocompleteProps {
  options: AutocompleteOption[];
  value?: string; // Controlled value
  defaultValue?: string; // Uncontrolled initial value
  placeholder?: string;
  emptyMessage?: string;
  onSelect?: (value: string) => void; // Backward compatibility
  onValueChange?: (value: string) => void; // Called on both select and clear
  disabled?: boolean;
  className?: string;
  showClearButton?: boolean; // Whether to show clear button when value exists
  // NEW OPTIONAL PROPS for server-side search support:
  isLoading?: boolean; // Show loading state while fetching
  onSearchChange?: (searchTerm: string) => void; // Notify parent of search input changes
  minSearchLength?: number; // Min chars before search (default: 0 = disabled)
  searchPlaceholder?: string; // Placeholder for search input
  onPopoverOpenChange?: (open: boolean) => void; // Notify when popover opens/closes
}

interface MultiselectAutocompleteProps {
  options: AutocompleteOption[];
  selectedItems: SelectedItem[];
  onSelectionChange: (items: SelectedItem[]) => void;
  placeholder?: string;
  emptyMessage?: string;
  searchPlaceholder?: string;
  isLoading?: boolean;
  onSearchChange?: (searchTerm: string) => void;
  minSearchLength?: number;
  className?: string;
}

// Enhanced single-select autocomplete with clear button and controlled value
export function Autocomplete({
  options,
  value: controlledValue,
  defaultValue,
  placeholder = 'Select an option',
  emptyMessage = 'No option found.',
  onSelect,
  onValueChange,
  disabled = false,
  className = '',
  showClearButton = true,
  // Server-side search props
  isLoading = false,
  onSearchChange,
  minSearchLength = 0,
  searchPlaceholder,
  onPopoverOpenChange,
}: AutocompleteProps) {
  const [open, setOpen] = React.useState(false);
  const [internalValue, setInternalValue] = React.useState(defaultValue || '');
  const [searchTerm, setSearchTerm] = React.useState('');
  const [triggerWidth, setTriggerWidth] = React.useState<number | undefined>();
  const triggerRef = React.useRef<HTMLDivElement>(null);

  // Use controlled value if provided, otherwise use internal state
  const value = controlledValue !== undefined ? controlledValue : internalValue;

  // Determine if we're using server-side search
  const useServerSideSearch = !!onSearchChange;

  // Update trigger width when popover opens
  React.useEffect(() => {
    if (open && triggerRef.current) {
      setTriggerWidth(triggerRef.current.offsetWidth);
    }
  }, [open]);

  // Handle popover open/close changes
  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen);
    onPopoverOpenChange?.(newOpen);
    // Note: Search term reset is handled by parent component to avoid race conditions
  };

  // Handle search term changes
  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    onSearchChange?.(value);
  };

  // Find the selected option to display its label
  const selectedOption = options.find((opt) => opt.value === value);
  const displayValue = selectedOption ? selectedOption.label : '';

  // Check if we should show the min search length message
  const showMinSearchMessage =
    useServerSideSearch &&
    minSearchLength > 0 &&
    searchTerm.length > 0 &&
    searchTerm.length < minSearchLength;

  const handleSelect = (selectedValue: string) => {
    // Update internal state if uncontrolled
    if (controlledValue === undefined) {
      setInternalValue(selectedValue);
    }

    // Call callbacks
    onSelect?.(selectedValue);
    onValueChange?.(selectedValue);

    // Close popover
    setOpen(false);
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();

    const newValue = '';

    // Update internal state if uncontrolled
    if (controlledValue === undefined) {
      setInternalValue(newValue);
    }

    // Call callbacks
    onValueChange?.(newValue);

    // Don't close popover on clear
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <div ref={triggerRef} className={cn('relative', className)}>
          <Button
            type="button"
            variant="outline"
            role="combobox"
            aria-expanded={open}
            disabled={disabled}
            className={cn(
              'w-full justify-between font-normal',
              !displayValue && 'text-muted-foreground',
              className,
            )}
          >
            <span className="truncate">{displayValue || placeholder}</span>
            <div className="flex items-center gap-1 ml-2 shrink-0">
              {showClearButton && value && !disabled && (
                <X
                  className="h-4 w-4 opacity-50 hover:opacity-100"
                  onClick={handleClear}
                  aria-label="Clear selection"
                />
              )}
              <Search className="h-4 w-4 opacity-50" />
            </div>
          </Button>
        </div>
      </PopoverTrigger>
      <PopoverContent className="p-0" style={{ width: triggerWidth }} align="start">
        <Command shouldFilter={!useServerSideSearch}>
          <CommandInput
            placeholder={searchPlaceholder || `Search ${placeholder.toLowerCase()}...`}
            value={useServerSideSearch ? searchTerm : undefined}
            onValueChange={useServerSideSearch ? handleSearchChange : undefined}
          />
          <CommandList>
            {showMinSearchMessage ? (
              <CommandEmpty>
                <span className="text-muted-foreground">
                  Type at least {minSearchLength} character{minSearchLength > 1 ? 's' : ''} to
                  search
                </span>
              </CommandEmpty>
            ) : isLoading ? (
              <CommandEmpty>
                <span className="text-muted-foreground">Searching...</span>
              </CommandEmpty>
            ) : options.length === 0 ? (
              <CommandEmpty>{emptyMessage}</CommandEmpty>
            ) : (
              <CommandGroup>
                {options.map((option) => (
                  <CommandItem
                    key={option.value}
                    value={option.value}
                    keywords={[option.label, option.description || '']}
                    onSelect={handleSelect}
                    className="flex items-center justify-between"
                  >
                    <div className="flex items-center flex-1 min-w-0">
                      <Check
                        className={cn(
                          'mr-2 h-4 w-4 shrink-0',
                          value === option.value ? 'opacity-100' : 'opacity-0',
                        )}
                      />
                      <div className="flex flex-col flex-1 min-w-0">
                        <span className="truncate">{option.label}</span>
                        {option.description && (
                          <span className="text-xs text-slate-500 dark:text-slate-400 truncate">
                            {option.description}
                          </span>
                        )}
                      </div>
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

// New multiselect autocomplete component
export function MultiselectAutocomplete({
  options,
  selectedItems,
  onSelectionChange,
  placeholder = 'Select items...',
  emptyMessage = 'No options found.',
  searchPlaceholder = 'Search...',
  isLoading = false,
  onSearchChange,
  minSearchLength = 1,
  className = '',
}: MultiselectAutocompleteProps) {
  const [open, setOpen] = React.useState(false);
  const [searchTerm, setSearchTerm] = React.useState('');
  const [triggerWidth, setTriggerWidth] = React.useState<number | undefined>();
  const triggerRef = React.useRef<HTMLButtonElement>(null);

  // Update search term and notify parent
  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    onSearchChange?.(value);
  };

  // Set popover width to match trigger
  React.useEffect(() => {
    if (open && triggerRef.current) {
      setTriggerWidth(triggerRef.current.offsetWidth);
    }
  }, [open]);

  const toggleSelection = (option: AutocompleteOption) => {
    const existing = selectedItems.find((item) => item.value === option.value);

    if (existing) {
      // Remove the item
      onSelectionChange(selectedItems.filter((item) => item.value !== option.value));
    } else {
      // Add the item
      const newItem: SelectedItem = {
        value: option.value,
        label: option.label,
        description: option.description,
      };
      onSelectionChange([...selectedItems, newItem]);
    }
  };

  const isSelected = (option: AutocompleteOption) => {
    return selectedItems.some((item) => item.value === option.value);
  };

  const removeItem = (item: SelectedItem) => {
    onSelectionChange(selectedItems.filter((selected) => selected.value !== item.value));
  };

  // Group options by their group property
  const groupedOptions = React.useMemo(() => {
    const groups: Record<string, AutocompleteOption[]> = {};

    options.forEach((option) => {
      const groupName = option.group || 'default';
      if (!groups[groupName]) {
        groups[groupName] = [];
      }
      groups[groupName].push(option);
    });

    return groups;
  }, [options]);

  return (
    <div className={cn('space-y-2', className)}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            ref={triggerRef}
            variant="outline"
            className="w-full justify-between text-left font-normal bg-white text-muted"
          >
            {selectedItems.length > 0 ? `${selectedItems.length} items selected` : placeholder}
            <Search className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="p-0" style={{ width: triggerWidth }} align="start">
          <Command shouldFilter={false}>
            <CommandInput
              placeholder={searchPlaceholder}
              value={searchTerm}
              onValueChange={handleSearchChange}
            />
            <CommandList>
              {searchTerm.length < minSearchLength ? (
                <CommandEmpty>
                  <span className="text-muted">
                    Type at least {minSearchLength} character{minSearchLength > 1 ? 's' : ''} to
                    search
                  </span>
                </CommandEmpty>
              ) : isLoading ? (
                <CommandEmpty>Searching...</CommandEmpty>
              ) : options.length === 0 ? (
                <CommandEmpty>{emptyMessage}</CommandEmpty>
              ) : (
                <>
                  {Object.entries(groupedOptions).map(([groupName, groupOptions]) => (
                    <CommandGroup
                      key={groupName}
                      heading={groupName !== 'default' ? groupName : undefined}
                    >
                      {groupOptions.map((option) => (
                        <CommandItem
                          key={option.value}
                          onSelect={() => toggleSelection(option)}
                          className="flex items-center space-x-2"
                        >
                          <Checkbox checked={isSelected(option)} />
                          <div className="flex flex-col flex-1 min-w-0">
                            <span className="font-medium truncate">{option.label}</span>
                            {option.description && (
                              <span className="text-xs text-slate-500 dark:text-slate-400 truncate">
                                {option.description}
                              </span>
                            )}
                          </div>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  ))}
                </>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {/* Display selected items as badges */}
      {selectedItems.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {selectedItems.map((item) => (
            <Badge key={item.value} variant="secondary" className="max-w-xs">
              <span className="truncate">{item.label}</span>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="ml-1 h-auto p-0 hover:bg-transparent"
                onClick={() => removeItem(item)}
              >
                <X className="w-3 h-3" />
              </Button>
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
}
